import React, { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FiFilter } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { Button, Form, Col } from 'reactstrap';

import DefaultFieldsFilterForm from './DefaultFieldsFilterForm';
import { buttonStyle } from 'components/Containers/ConfigurationContainer/components/utils';
import { customFieldTypes } from 'views/fieldConfigs/utils/customFieldTypes';
import { parseIntFromString } from 'utils/fuctions/parseIntFromString';
import { useMemorizeFilters } from '../../../../../../../../hooks/useMemorizeInputsFilters';
import { AiOutlineClear } from 'react-icons/ai';
import { resetToFirstDayOfMonth } from '../../../../../../shared/helpers/getFirstDayOfTheMonth';
import { resetFields } from '../../../../../../shared/helpers/resetInputsFIeldsValues';
import xlsxImage from '../../../../../../../../assets/img/xlsx.png';
import csvImage from '../../../../../../../../assets/img/csv.png';
import NotPurchasedReportService from '../../../../../services/notPurchasedReportService';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';
import { Tooltip } from '@material-ui/core';
import {
  ImageButton,
  ImageSize
} from '../../../../../../../../assets/styles/image_style';
import Loader from 'react-loader-spinner';

const pageKey = 'flexTableConfig::[notPurchased]';

const FilterNotPurchasedReportForm = ({
  handleSearch,
  customFields,
  isLoadingNotPurchased,
  selectedRecordTypeId,
  setSelectedRecordType,
  recordTypes,
  recordTypeId,
  notPurchaseds,
  totalNotPurchased
}) => {
  const [analyticalData, setAnalyticalData] = useState([]);
  const [loadingCSV, setLoadingCSV] = useState(false);
  const [loadingXLSX, setLoadingXLSX] = useState(false);

  const {
    setValue,
    control,
    getValues,
    handleSubmit,
    register,
    reset,
    errors
  } = useForm({
    mode: 'onChange'
  });
  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  const { memorizeFilters, getMemorizedFilters, clearMemorizedFilters } =
    useMemorizeFilters('CRM - NotPurchasedReport');

  useEffect(() => {
    const filters = getMemorizedFilters();
    if (filters) {
      reset(filters);
    } else if (customFields) {
      customFields.forEach((field) => {
        if (field) {
          setValue(field.name, '');
        }
      });
    }
  }, [customFields, getMemorizedFilters, reset, resetConfig, setValue]);

  useEffect(() => {
    if (totalNotPurchased) {
      setAnalyticalData(totalNotPurchased);
    }
  }, [notPurchaseds]);

  const saveFiltersWithRecordType = (filters) => {
    const filtersWithRecordType = {
      ...filters,
      recordTypeId: selectedRecordTypeId
    };
    memorizeFilters(filtersWithRecordType);
  };

  const onSubmit = async (data) => {
    saveFiltersWithRecordType(data);
    const currencyFieldsName =
      customFields &&
      customFields
        .filter((field) => field.type === customFieldTypes.CURRENCY)
        .map((field) => field.name);

    let formattedData = { ...data };

    Object.keys(data).forEach((key) => {
      if (currencyFieldsName.includes(key)) {
        if (data[key]) {
          formattedData[key] = parseIntFromString(data[key]);
        }
      }
    });

    await handleSearch(formattedData);
  };
  const resetConfig = useMemo(
    () => ({
      dateBegin: resetToFirstDayOfMonth,
      dateEnd: () => new Date().toLocaleDateString('pt-br').toString(),
      filterDateBy: () => 'creation',
      createdByLogin: () => '',
      textQuery: () => '',
      tags: () => []
    }),
    []
  );

  const handleReset = () => {
    if (selectedRecordTypeId && recordTypes && recordTypes.length) {
      setSelectedRecordType(recordTypes[0]);
    }
    resetFields(resetConfig, customFields, control);
    clearMemorizedFilters();
  };

  const formatColumnName = (name) => {
    return name.replace(/([A-Z])/g, '_$1').toLowerCase();
  };

  const handleReportExport = (typeExport, e) => {
    e.preventDefault();

    const params = getValues();

    const columnConfig = JSON.parse(localStorage.getItem(pageKey)) || [];
    const orderByConfig = columnConfig.find((col) => col.isSort);
    const orderBy = orderByConfig ? formatColumnName(orderByConfig.key) : null;
    const orderType = orderByConfig
      ? orderByConfig.sortDirection === 'ascending'
        ? 'ASC'
        : 'DESC'
      : null;
    const hiddenColumns = (columnConfig || [])
      .filter((col) => !col.visible)
      .map((col) => formatColumnName(col.key));

    const columnsConfig = localStorage.getItem(pageKey);

    const columnsOrder = columnsConfig
      ? JSON.parse(columnsConfig).map((col) => formatColumnName(col.key))
      : [];

    const dto = {
      ...params,
      initialDate: encodeURI(params.dateBegin),
      finalDate: encodeURI(params.dateEnd),
      recordTypeId,
      orderBy,
      orderType,
      hiddenColumn: hiddenColumns,
      columnsOrder: encodeURIComponent(columnsOrder)
    };

    if (params && params.tags) dto.tags = params.tags.map((tag) => tag.value);

    if (typeExport == 'XLSX') {
      setLoadingXLSX(true);

      NotPurchasedReportService.getExcel(dto)
        .then((res) => {
          if (res.data.size === 0) {
            setLoadingCSV(false);
            toast.info('Nenhum registro foi encontrado!');
          } else {
            saveAs(
              new Blob([res.data], {
                type: 'application/octet-stream'
              }),
              `relatorio-ausencia-de-compras.xlsx`
            );
          }

          setLoadingXLSX(false);
        })
        .catch(() => {
          setLoadingXLSX(false);
          toast.info('Nenhum registro foi encontrado!');
        });
    } else if (typeExport == 'CSV') {
      setLoadingCSV(true);

      NotPurchasedReportService.getCsv(dto)
        .then((res) => {
          if (res.data.size === 0) {
            setLoadingCSV(false);
            toast.info('Nenhum registro foi encontrado!');
          } else {
            saveAs(
              new Blob([res.data], { type: 'application/octet-stream' }),
              `relatorio-ausencia-de-compras.csv`
            );

            setLoadingCSV(false);
          }
        })
        .catch(() => {
          setLoadingCSV(false);
          toast.info('Nenhum registro foi encontrado!');
        });
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div
        style={{
          position: 'absolute',
          right: '1rem',
          top: '2rem'
        }}
      >
        <span
          onClick={() => {
            handleReset();
          }}
          className="small"
          style={{ cursor: 'pointer' }}
        >
          <AiOutlineClear /> Limpar filtros
        </span>
      </div>
      <DefaultFieldsFilterForm
        handleSearch={handleSearch}
        setValue={setValue}
        control={control}
        getValues={getValues}
        register={register}
        errors={errors}
      />

      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Button
            style={buttonStyle(systemConfiguration.primary_color)}
            type="submit"
            size="md"
            disabled={isLoadingNotPurchased}
          >
            <FiFilter />
            <span className="ml-1">Buscar</span>
          </Button>
          <Col
            md="6"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end'
            }}
          >
            <button
              id="button_csv"
              style={ImageButton}
              onClick={(e) => {
                handleReportExport('CSV', e);
              }}
              disabled={analyticalData && !loadingCSV ? false : true}
            >
              <Tooltip
                title={
                  <p style={{ fontSize: '14px' }}>
                    {analyticalData
                      ? `Fazer download do relatório de Ausência de Compras em CSV.`
                      : 'Nenhum registro para realizar download'}
                  </p>
                }
                arrow
              >
                {loadingCSV ? (
                  <Loader
                    type="ThreeDots"
                    visible={true}
                    height="40"
                    width="40"
                  />
                ) : (
                  <img src={csvImage} alt="csv" style={ImageSize} />
                )}
              </Tooltip>
            </button>
            <button
              id="button_xlsx"
              style={ImageButton}
              onClick={(e) => {
                handleReportExport('XLSX', e);
              }}
              disabled={analyticalData && !loadingXLSX ? false : true}
            >
              <Tooltip
                title={
                  <p style={{ fontSize: '14px' }}>
                    {analyticalData
                      ? `Fazer download do relatório de Contatos em XLSX.`
                      : 'Nenhum registro para realizar download'}
                  </p>
                }
                arrow
              >
                {loadingXLSX ? (
                  <Loader
                    type="ThreeDots"
                    visible={true}
                    height="40"
                    width="40"
                  />
                ) : (
                  <img src={xlsxImage} alt="xlsx" style={ImageSize} />
                )}
              </Tooltip>
            </button>
          </Col>
        </div>
        <div>
          <div style={{ fontSize: 'small', float: 'right' }}>
            {totalNotPurchased} resultado(s) encontrado(s)
          </div>
        </div>
      </div>
    </Form>
  );
};

export default FilterNotPurchasedReportForm;
